import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== "string") return [31, 122, 92];
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(n, 16);
  if (Number.isNaN(int)) return [31, 122, 92];
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};

const cellVal = (col, row) => {
  const v = typeof col.accessor === "function" ? col.accessor(row) : row[col.accessor];
  return v === null || v === undefined ? "" : v;
};

const stamp = () => new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const safe = (s) => String(s || "export").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Disegna il logo aziendale (data URL) in alto a destra dell'header PDF, se presente.
function _pdfLogo(doc, logo, W, M, top = 3, maxH = 14) {
  if (!logo) return;
  try {
    const props = doc.getImageProperties(logo);
    const ratio = props.width && props.height ? props.width / props.height : 1;
    let h = maxH, w = h * ratio;
    const maxW = 34;
    if (w > maxW) { w = maxW; h = w / ratio; }
    doc.addImage(logo, (props.fileType || "PNG"), W - M - w, top, w, h);
  } catch { /* logo non renderizzabile: ignora */ }
}

// Header con barra colorata + brand: se c'è il logo lo mette in alto a destra e il titolo a sinistra,
// altrimenti mantiene il riquadro "GR" storico. Usato dalle esportazioni tabellari/report.
function _pdfBrandHeader(doc, { W, rgb, brand = "Gestione Residenze", title = "", logo = "", barH = 22 }) {
  doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  doc.rect(0, 0, W, barH, "F");
  if (logo) {
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(brand, 12, 11);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text(title || "", 12, 17);
    _pdfLogo(doc, logo, W, 12, 4, 14);
  } else {
    doc.setFillColor(255, 255, 255); doc.roundedRect(12, 6, 10, 10, 2, 2, "F");
    doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("GR", 17, 12.5, { align: "center" });
    doc.setTextColor(255, 255, 255); doc.setFontSize(13); doc.text(brand, 27, 11);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text(title || "", 27, 17);
  }
}

export function exportData({ format, filename, title, subtitle, columns, rows, color, logo = "" }) {
  const headers = columns.map((c) => c.header);
  const body = rows.map((r) => columns.map((c) => cellVal(c, r)));
  const fname = `${safe(filename || title)}_${new Date().toISOString().slice(0, 10)}`;
  if (format === "excel") exportExcel({ fname, title, subtitle, headers, body });
  else exportPDF({ fname, title, subtitle, headers, body, color, logo });
}

function exportExcel({ fname, title, subtitle, headers, body }) {
  const aoa = [["Gestione Residenze"], [title || ""]];
  if (subtitle) aoa.push([subtitle]);
  aoa.push([`Esportato il ${stamp()}`]);
  aoa.push([]);
  aoa.push(headers);
  body.forEach((r) => aoa.push(r));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = headers.map((h, i) => ({ wch: Math.min(40, Math.max(12, ...body.map((r) => String(r[i] ?? "").length), String(h).length + 2)) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Dati");
  XLSX.writeFile(wb, `${fname}.xlsx`);
}

function exportPDF({ fname, title, subtitle, headers, body, color, logo = "" }) {
  const landscape = headers.length > 6;
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const rgb = hexToRgb(color);
  _pdfBrandHeader(doc, { W, rgb, title, logo });
  doc.setTextColor(90, 90, 90); doc.setFontSize(8.5);
  const sub = [subtitle, `Esportato il ${stamp()}`].filter(Boolean).join("   ·   ");
  doc.text(sub, 12, 28);
  autoTable(doc, {
    head: [headers],
    body,
    startY: 32,
    styles: { fontSize: 8, cellPadding: 1.8, overflow: "linebreak" },
    headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 248, 246] },
    margin: { left: 10, right: 10 },
    didDrawPage: (d) => {
      const page = doc.internal.getNumberOfPages();
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text(`Pagina ${page}`, W - 10, doc.internal.pageSize.getHeight() - 6, { align: "right" });
    },
  });
  doc.save(`${fname}.pdf`);
}

// Report per il commercialista: elenco ospiti con retta pagata del mese (opz. raggruppato per struttura)
export function exportCommercialista({ format, subtitle, meseLabel, righe, grouped, color, logo = "" }) {
  const tot = righe.reduce((a, r) => a + Number(r.pagato || 0), 0);
  const fname = `commercialista_${safe(subtitle)}_${safe(meseLabel)}`;
  const groups = {};
  if (grouped) righe.forEach((r) => { const k = r.struttura || "—"; (groups[k] = groups[k] || []).push(r); });
  const groupKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, "it"));

  if (format === "excel") {
    const aoa = [["Gestione Residenze — Report Commercialista"], [subtitle || ""], [meseLabel || ""], [`Esportato il ${stamp()}`], []];
    if (grouped) {
      groupKeys.forEach((k) => {
        const rows = groups[k];
        const sub = rows.reduce((a, r) => a + Number(r.pagato || 0), 0);
        aoa.push([`Struttura: ${k}`]);
        aoa.push(["Cognome", "Nome", "Retta pagata"]);
        rows.forEach((r) => aoa.push([r.cognome || "", r.nome || "", Number(r.pagato || 0)]));
        aoa.push([`Subtotale ${k}`, "", Number(sub.toFixed(2))]);
        aoa.push([]);
      });
      aoa.push(["TOTALE GENERALE", "", Number(tot.toFixed(2))]);
    } else {
      aoa.push(["Cognome", "Nome", "Retta pagata"]);
      righe.forEach((r) => aoa.push([r.cognome || "", r.nome || "", Number(r.pagato || 0)]));
      aoa.push(["TOTALE", "", Number(tot.toFixed(2))]);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 26 }, { wch: 20 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rette pagate");
    XLSX.writeFile(wb, `${fname}.xlsx`);
    return;
  }
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const rgb = hexToRgb(color);
  _pdfBrandHeader(doc, { W, rgb, brand: "Report Commercialista", title: [subtitle, meseLabel].filter(Boolean).join("  ·  "), logo });
  doc.setTextColor(90, 90, 90); doc.setFontSize(8.5);
  doc.text(`Esportato il ${stamp()}`, 12, 28);
  const pageNum = () => { const p = doc.internal.getNumberOfPages(); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Pagina ${p}`, W - 10, doc.internal.pageSize.getHeight() - 6, { align: "right" }); };
  const common = {
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [235, 240, 237], textColor: 30, fontStyle: "bold" },
    columnStyles: { 2: { halign: "right" } },
    margin: { left: 10, right: 10 },
    didDrawPage: pageNum,
  };
  if (grouped) {
    let first = true;
    groupKeys.forEach((k) => {
      const rows = groups[k];
      const sub = rows.reduce((a, r) => a + Number(r.pagato || 0), 0);
      const y = first ? 34 : doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11); doc.setTextColor(30, 30, 30); doc.setFont("helvetica", "bold");
      doc.text(k, 10, y);
      autoTable(doc, {
        head: [["Cognome", "Nome", "Retta pagata"]],
        body: rows.map((r) => [r.cognome || "", r.nome || "", eur(r.pagato)]),
        foot: [["Subtotale", "", eur(sub)]],
        startY: y + 2,
        ...common,
      });
      first = false;
    });
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(rgb[0], rgb[1], rgb[2]);
    doc.text(`TOTALE GENERALE: ${eur(tot)}`, W - 10, doc.lastAutoTable.finalY + 10, { align: "right" });
  } else {
    autoTable(doc, {
      head: [["Cognome", "Nome", "Retta pagata"]],
      body: righe.map((r) => [r.cognome || "", r.nome || "", eur(r.pagato)]),
      foot: [["TOTALE", "", eur(tot)]],
      startY: 32,
      ...common,
    });
  }
  doc.save(`${fname}.pdf`);
}


// Report fiscale annuale per il commercialista: Ricavi-Costi, rettifiche fiscali, ammortamenti, imposte
export function exportReportFiscale({ anno, subtitle, righe, totals, aliquota, color = "#e11d48", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  _pdfBrandHeader(doc, { W, rgb, title: `Report fiscale annuale ${anno}`, logo });
  doc.setTextColor(90, 90, 90); doc.setFontSize(8.5);
  const sub = [subtitle, `Aliquota ${aliquota}%`, `Esportato il ${stamp()}`].filter(Boolean).join("   ·   ");
  doc.text(sub, 12, 28);
  const headers = ["Mese", "Ricavi - Costi", "Rettifiche fisc.", "Interessi mutui", "Quota capitale", "Ammortamenti", "Ante imposte", `Tasse (${aliquota}%)`, "Utile netto", "Cassa effettiva"];
  const body = righe.map((r) => [
    r.label,
    r.haDati ? eur(r.utileRC) : "-",
    r.rettifiche ? `+ ${eur(r.rettifiche)}` : "-",
    r.interessi ? `- ${eur(r.interessi)}` : "-",
    r.capitale ? eur(r.capitale) : "-",
    r.amm ? `- ${eur(r.amm)}` : "-",
    r.haDati ? eur(r.anteImposte) : "-",
    eur(r.tasse),
    r.haDati ? eur(r.utileNetto) : "-",
    r.haDati ? eur(r.cassa) : "-",
  ]);
  const foot = [[`Totale ${anno}`, eur(totals.rc), `+ ${eur(totals.rettifiche)}`, `- ${eur(totals.interessi)}`, eur(totals.capitale), `- ${eur(totals.amm)}`, eur(totals.anteImposte), eur(totals.tasse), eur(totals.utileNetto), eur(totals.cassa)]];
  autoTable(doc, {
    head: [headers], body, foot, startY: 32,
    styles: { fontSize: 7.5, cellPadding: 1.6, halign: "right", overflow: "linebreak" },
    columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold", halign: "right" },
    footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold", halign: "right" },
    alternateRowStyles: { fillColor: [250, 247, 248] },
    margin: { left: 10, right: 10 },
  });
  let y = doc.lastAutoTable.finalY + 8;
  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - 70) { doc.addPage(); y = 20; }
  doc.setFontSize(11); doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setFont("helvetica", "bold");
  doc.text("Riepilogo fiscale dell'anno", 12, y); y += 6;
  doc.setFont("helvetica", "normal"); doc.setTextColor(40, 40, 40); doc.setFontSize(9);
  const lines = [
    ["Ricavi - Costi (gestionale, a costi pieni)", eur(totals.rc)],
    ["+ Spese capitalizzate (tolte dai costi)", eur(totals.cap)],
    ["+ Quota indeducibile spese", eur(totals.indeduc)],
    ["- Interessi mutui", eur(totals.interessi)],
    ["- Ammortamenti (quota deducibile)", eur(totals.amm)],
    ["= Utile ante imposte", eur(totals.anteImposte)],
    [`- Tasse (${aliquota}%)`, eur(totals.tasse)],
    ["= Utile netto", eur(totals.utileNetto)],
    ["Quota capitale mutui (rimborso debito)", eur(totals.capitale)],
    ["Cassa effettiva dell'anno", eur(totals.cassa)],
  ];
  lines.forEach(([l, v]) => { doc.text(l, 14, y); doc.text(v, 120, y, { align: "right" }); y += 5.6; });
  y += 2; doc.setFontSize(7.5); doc.setTextColor(150);
  doc.text("Valori gestionali indicativi. Le rettifiche fiscali e le imposte effettive vanno confermate con il commercialista.", 12, y);
  doc.save(`report-fiscale-${anno}.pdf`);
}

// Guida operativa in PDF (testo strutturato multi-pagina)
export function exportGuida({ title, sottotitolo, gruppi, color = "#1f7a5c", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 16; let y = 0;
  const header = () => {
    doc.setFillColor(rgb[0], rgb[1], rgb[2]); doc.rect(0, 0, W, 20, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("Gestione Residenze", M, 9);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text(title, M, 15.5);
    _pdfLogo(doc, logo, W, M, 3, 14);
    y = 27;
  };
  const ensure = (h) => { if (y + h > H - 14) { doc.addPage(); header(); } };
  header();
  if (sottotitolo) { doc.setTextColor(110, 110, 110); doc.setFontSize(9); doc.text(sottotitolo, M, y); y += 7; }
  gruppi.forEach((g) => {
    ensure(12);
    doc.setFillColor(240, 244, 242); doc.rect(M - 3, y - 4, W - 2 * (M - 3), 8, "F");
    doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setFont("helvetica", "bold"); doc.setFontSize(11.5);
    doc.text(g.titolo, M, y + 1.5); y += 10;
    g.voci.forEach((v) => {
      ensure(10);
      doc.setTextColor(30, 30, 30); doc.setFont("helvetica", "bold"); doc.setFontSize(10.5);
      doc.text(v.h, M, y); y += 5;
      if (v.d) { doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(110, 110, 110);
        const dl = doc.splitTextToSize(v.d, W - 2 * M); ensure(dl.length * 4.5); doc.text(dl, M, y); y += dl.length * 4.5 + 1; }
      doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(40, 40, 40);
      (v.steps || []).forEach((s, i) => {
        const lines = doc.splitTextToSize(`${i + 1}. ${s}`, W - 2 * M - 4);
        ensure(lines.length * 4.6); doc.text(lines, M + 4, y); y += lines.length * 4.6;
      });
      y += 3;
    });
    y += 2;
  });
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · pag. ${i}/${pages} · ${stamp()}`, M, H - 8); }
  doc.save(`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
}


function _loadImgForPdf(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve({ dataURL: canvas.toDataURL("image/jpeg", 0.82), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// Guida illustrata (A4 verticale, pronta per la bacheca): titolo + descrizione + screenshot + passi
export async function exportGuidaIllustrata({ title, sottotitolo, sezioni, color = "#1f7a5c", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 16; const CW = W - 2 * M; let y = 0;
  const header = () => {
    doc.setFillColor(rgb[0], rgb[1], rgb[2]); doc.rect(0, 0, W, 20, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("Gestione Residenze", M, 9);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text(title, M, 15.5);
    _pdfLogo(doc, logo, W, M, 3, 14);
    y = 27;
  };
  const ensure = (h) => { if (y + h > H - 14) { doc.addPage(); header(); } };
  header();
  if (sottotitolo) { doc.setTextColor(110, 110, 110); doc.setFontSize(9); doc.text(sottotitolo, M, y); y += 8; }

  // preload immagini
  const cache = {};
  for (const s of sezioni) { if (s.img && !(s.img in cache)) cache[s.img] = await _loadImgForPdf(s.img); }

  for (const s of sezioni) {
    ensure(14);
    doc.setFillColor(240, 244, 242); doc.rect(M - 3, y - 4, W - 2 * (M - 3), 8.5, "F");
    doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text(s.titolo, M, y + 1.8); y += 11;
    if (s.testo) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(9.5); doc.setTextColor(90, 90, 90);
      const dl = doc.splitTextToSize(s.testo, CW); ensure(dl.length * 4.6); doc.text(dl, M, y); y += dl.length * 4.6 + 2;
    }
    const im = s.img ? cache[s.img] : null;
    if (im) {
      let dW = CW, dH = CW * (im.h / im.w);
      const maxH = 150;
      if (dH > maxH) { dH = maxH; dW = maxH * (im.w / im.h); }
      ensure(dH + 4);
      const x = M + (CW - dW) / 2;
      doc.setDrawColor(220); doc.rect(x, y, dW, dH);
      try { doc.addImage(im.dataURL, "JPEG", x, y, dW, dH); } catch { /* skip */ }
      y += dH + 4;
    }
    (s.steps || []).forEach((st, i) => {
      doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(`${i + 1}. ${st}`, CW - 4);
      ensure(lines.length * 4.6); doc.text(lines, M + 4, y); y += lines.length * 4.6;
    });
    y += 5;
  }
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · pag. ${i}/${pages} · ${stamp()}`, M, H - 8); }
  doc.save(`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-illustrata.pdf`);
}

// Foglio di consegna credenziali per il check-in di un operatore (A4 verticale)
export function exportCheckinCredenziali({ operatore, struttura, link, email, password, pin, color = "#1f7a5c", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 18; const CW = W - 2 * M;
  // Header
  doc.setFillColor(rgb[0], rgb[1], rgb[2]); doc.rect(0, 0, W, 26, "F");
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text("Gestione Residenze", M, 12);
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.text("Credenziali di accesso — consegna al check-in", M, 20);
  _pdfLogo(doc, logo, W, M, 5, 16);
  let y = 40;
  doc.setTextColor(30, 30, 30); doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  doc.text("Operatore:", M, y);
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.text(operatore || "—", M + 26, y);
  if (struttura) { doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(110, 110, 110); doc.text(`Struttura: ${struttura}`, M, y + 6); }
  y += 16;

  // Box credenziali
  const rows = [
    ["Link del sito", link || "—"],
    ["Email", email || "—"],
    ["Password", password || "—"],
    ["PIN personale", pin || "—"],
  ];
  const rowH = 16;
  doc.setDrawColor(210); doc.setLineWidth(0.3);
  rows.forEach((r, i) => {
    const ry = y + i * rowH;
    doc.setFillColor(i % 2 ? 248 : 240, i % 2 ? 250 : 244, i % 2 ? 249 : 242);
    doc.rect(M, ry, CW, rowH, "F"); doc.rect(M, ry, CW, rowH);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(110, 110, 110);
    doc.text(r[0].toUpperCase(), M + 4, ry + 6);
    doc.setFont("helvetica", "bold"); doc.setFontSize(r[0] === "PIN personale" ? 18 : 12);
    doc.setTextColor(r[0] === "PIN personale" ? rgb[0] : 20, r[0] === "PIN personale" ? rgb[1] : 20, r[0] === "PIN personale" ? rgb[2] : 20);
    const val = doc.splitTextToSize(String(r[1]), CW - 8);
    doc.text(val[0], M + 4, ry + 12.5);
  });
  y += rows.length * rowH + 12;

  // Istruzioni
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  doc.text("Come accedere", M, y); y += 8;
  const steps = [
    "Apri il link del sito nel browser (telefono, tablet o computer).",
    "Inserisci l'email e la password qui sopra e tocca «Accedi».",
    "Nella schermata della Firma Rapida, CLICCA SUL TUO NOME nella lista degli operatori.",
    "Digita il tuo PIN personale per confermare che sei tu.",
    "Da questo momento tutte le attività che registri risultano firmate a tuo nome.",
    "Puoi cambiare il PIN quando vuoi da «Il mio account». Il PIN è personale: non condividerlo.",
  ];
  doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(40, 40, 40);
  steps.forEach((s, i) => {
    const lines = doc.splitTextToSize(`${i + 1}.  ${s}`, CW - 2);
    doc.text(lines, M, y); y += lines.length * 5.4 + 1.5;
  });
  y += 6;
  doc.setDrawColor(230); doc.line(M, y, W - M, y); y += 6;
  doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(120, 120, 120);
  doc.text("Conserva questo foglio in un posto sicuro. In caso di smarrimento delle credenziali, rivolgiti all'amministratore.", M, y, { maxWidth: CW });

  const H = doc.internal.pageSize.getHeight();
  doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · ${stamp()}`, M, H - 10);
  doc.save(`credenziali-${safe(operatore)}.pdf`);
}

// Report mensile segnalazioni per categoria (da allegare alle riunioni)
export function exportSegnalazioniReport({ mese, strutturaNome = "", perCategoria = {}, color = "#d97706", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 14; const CW = W - 2 * M;
  const CAT_LABEL = {
    alvo: "Alvo intestinale", alzate: "Alzate", messa_a_letto: "Messa a letto",
    controllo_terapie: "Controllo terapie", non_mangia: "Non mangia", non_beve: "Non beve",
    docce: "Docce", parametri: "Parametri vitali", infettive: "Malattie infettive",
  };
  const meseLabel = (() => { try { return new Date(mese + "-01T00:00:00").toLocaleDateString("it-IT", { month: "long", year: "numeric" }); } catch { return mese; } })();
  _pdfBrandHeader(doc, { W, rgb, brand: "Report segnalazioni", title: `${meseLabel}${strutturaNome ? " · " + strutturaNome : ""}`, logo, barH: 24 });
  let y = 32;

  const cats = Object.keys(perCategoria).sort((a, b) => (CAT_LABEL[a] || a).localeCompare(CAT_LABEL[b] || b));
  const totAperte = cats.reduce((s, c) => s + (perCategoria[c].aperte || 0), 0);
  const totChiuse = cats.reduce((s, c) => s + (perCategoria[c].chiuse || 0), 0);
  doc.setTextColor(70, 70, 70); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text(`Totale segnalazioni: ${totAperte + totChiuse}   ·   aperte ${totAperte}   ·   risolte ${totChiuse}`, M, y); y += 7;

  if (cats.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setTextColor(150); doc.setFontSize(10);
    doc.text("Nessuna segnalazione registrata in questo mese.", M, y);
  }

  const fmtGiorno = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }); } catch { return iso || ""; } };

  cats.forEach((c) => {
    const info = perCategoria[c];
    if (y > H - 40) { doc.addPage(); y = 20; }
    doc.setFillColor(rgb[0], rgb[1], rgb[2]); doc.roundedRect(M, y, CW, 8, 1.5, 1.5, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(10.5);
    doc.text(`${CAT_LABEL[c] || c}`, M + 3, y + 5.5);
    doc.text(`aperte ${info.aperte || 0} · risolte ${info.chiuse || 0}`, W - M - 3, y + 5.5, { align: "right" });
    y += 11;
    const body = (info.items || []).map((d) => {
      const note = d.note || [];
      const ultima = note.length ? note[note.length - 1].testo : "";
      const stato = d.stato === "chiusa" ? `Risolta${d.chiusa_da ? " · " + d.chiusa_da : ""}` : "Aperta";
      return [d.ospite_nome || "—", fmtGiorno(d.data), String(note.length), ultima, stato];
    });
    autoTable(doc, {
      startY: y,
      head: [["Ospite", "Giorno", "Note", "Ultima segnalazione", "Stato"]],
      body: body.length ? body : [["", "", "", "Nessun dettaglio", ""]],
      styles: { fontSize: 8.5, cellPadding: 1.8, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: [90, 90, 90], textColor: 255, fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: [250, 247, 240] },
      columnStyles: { 0: { cellWidth: 34 }, 1: { cellWidth: 16, halign: "center" }, 2: { cellWidth: 12, halign: "center" }, 3: { cellWidth: CW - 34 - 16 - 12 - 30 }, 4: { cellWidth: 30 } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 7;
  });

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · Report segnalazioni · pag. ${i}/${pages} · ${stamp()}`, M, H - 8); }
  doc.save(`report-segnalazioni-${safe(mese + "-" + strutturaNome)}.pdf`);
}

// Verbale di riunione in PDF: intestazione, giorno, partecipanti (presente/assente) e punti con note e decisioni
export function exportRiunione({ riunione: r, strutturaNome = "", color = "#0f766e", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 14; const CW = W - 2 * M;
  _pdfBrandHeader(doc, { W, rgb, brand: "Verbale riunione", title: r.titolo || "Riunione", logo, barH: 24 });
  let y = 32;
  // Meta: giorno · ora · luogo · struttura
  const giorno = (() => { try { return new Date((r.data || "") + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return r.data || ""; } })();
  const meta = [giorno, r.ora ? `ore ${r.ora}` : "", r.luogo, strutturaNome].filter(Boolean).join("   ·   ");
  doc.setTextColor(70, 70, 70); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
  doc.text(meta, M, y); y += 5;
  if (r.organizzatore) { doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(110, 110, 110); doc.text(`Organizzatore: ${r.organizzatore}`, M, y); y += 5; }
  y += 2;

  // Partecipanti
  const lista = Array.isArray(r.partecipanti_lista) ? r.partecipanti_lista : [];
  const presenti = lista.filter((p) => p.presente);
  doc.setFillColor(240, 244, 242); doc.rect(M - 2, y - 4, CW + 4, 8, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  doc.text(`Partecipanti  ·  presenti ${presenti.length}/${lista.length}`, M, y + 1.6); y += 11;
  doc.setFontSize(9.5);
  if (lista.length === 0 && !(r.partecipanti_extra || "").trim()) {
    doc.setFont("helvetica", "italic"); doc.setTextColor(150); doc.text("Nessun partecipante indicato.", M, y); y += 6;
  } else {
    lista.forEach((p) => {
      if (y > H - 20) { doc.addPage(); y = 20; }
      const ok = !!p.presente;
      doc.setTextColor(ok ? 6 : 190, ok ? 95 : 60, ok ? 70 : 60);
      doc.setFont("helvetica", "bold"); doc.text(ok ? "[X]" : "[  ]", M, y);
      doc.setFont("helvetica", "normal"); doc.setTextColor(30, 30, 30);
      doc.text(`${p.nome || ""}${ok ? "" : "  (assente)"}`, M + 10, y);
      y += 5.4;
    });
    if ((r.partecipanti_extra || "").trim()) {
      if (y > H - 20) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(90, 90, 90);
      const el = doc.splitTextToSize(`Altri partecipanti: ${r.partecipanti_extra}`, CW);
      doc.text(el, M, y); y += el.length * 4.6;
    }
  }
  y += 4;

  // Punti all'ordine del giorno
  const punti = (r.punti || []).map((p, i) => [
    String(i + 1),
    [p.testo || "", (p.note || "").trim() ? `Note: ${p.note}` : ""].filter(Boolean).join("\n"),
    (p.decisione || "").trim() || "—",
  ]);
  autoTable(doc, {
    startY: y,
    head: [["#", "Punto all'ordine del giorno", "Decisione presa"]],
    body: punti.length ? punti : [["", "Nessun punto inserito.", ""]],
    styles: { fontSize: 9, cellPadding: 2.2, overflow: "linebreak", valign: "top" },
    headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 248, 246] },
    columnStyles: { 0: { cellWidth: 8, halign: "center", fontStyle: "bold" }, 1: { cellWidth: (CW - 8) * 0.55 }, 2: { cellWidth: (CW - 8) * 0.45 } },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 8;

  if ((r.note || "").trim()) {
    if (y > H - 30) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(rgb[0], rgb[1], rgb[2]);
    doc.text("Note generali", M, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(50, 50, 50);
    const nl = doc.splitTextToSize(r.note, CW);
    doc.text(nl, M, y); y += nl.length * 4.8;
  }
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · Verbale riunione · pag. ${i}/${pages} · ${stamp()}`, M, H - 8); }
  doc.save(`verbale-riunione-${safe((r.data || "") + "-" + (r.titolo || ""))}.pdf`);
}

// Comunicazione/lettera in PDF (da Email massiva) — pronta da condividere anche via WhatsApp
export function exportComunicazione({ subject, body, logo = "", heading = "", fromName = "", footer = "", color = "#1f7a5c" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 18; const CW = W - 2 * M;
  const brand = (heading || "").trim() || (fromName || "").trim() || "Gestione Residenze";
  _pdfBrandHeader(doc, { W, rgb, brand, title: "", logo });
  let y = 36;
  doc.setTextColor(90, 90, 90); doc.setFontSize(8.5);
  doc.text(stamp(), M, 28);
  // Oggetto come titolo
  doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  const st = doc.splitTextToSize(subject || "", CW);
  st.forEach((l) => { if (y > H - 20) { doc.addPage(); y = 20; } doc.text(l, M, y); y += 7; });
  y += 3;
  doc.setDrawColor(225); doc.line(M, y, W - M, y); y += 8;
  // Corpo
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(40, 40, 40);
  (body || "").split(/\n{2,}/).forEach((p) => {
    const lines = doc.splitTextToSize(p, CW);
    lines.forEach((line) => { if (y > H - 18) { doc.addPage(); y = 20; } doc.text(line, M, y); y += 6.2; });
    y += 4;
  });
  if ((footer || "").trim()) {
    if (y > H - 26) { doc.addPage(); y = 20; }
    y = Math.max(y + 6, H - 22);
    doc.setDrawColor(225); doc.line(M, y, W - M, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    const fl = doc.splitTextToSize(footer.trim(), CW);
    fl.forEach((line) => { doc.text(line, W / 2, y, { align: "center" }); y += 4.6; });
  }
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`pag. ${i}/${pages}`, W - M, H - 10, { align: "right" }); }
  doc.save(`comunicazione-${safe(subject || "messaggio")}.pdf`);
}




// Registro ingressi mensile: elenco dei registrati per struttura con conteggio vs posti accreditati
export function exportRegistroIngressi({ meseLabel, perStruct = [], color = "#0284c7", logo = "" }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 12;
  _pdfBrandHeader(doc, { W, rgb, brand: "Registro ingressi", title: meseLabel || "", logo, barH: 24 });
  const totReg = perStruct.reduce((s, p) => s + (p.registrati?.length || 0), 0);
  const totPosti = perStruct.reduce((s, p) => s + (Number(p.posti) || 0), 0);
  doc.setTextColor(70, 70, 70); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text(`Totale registrati: ${totReg} / ${totPosti} posti accreditati   ·   Esportato il ${stamp()}`, M, 31);

  let first = true;
  perStruct.forEach((p) => {
    const reg = p.registrati || [];
    const posti = Number(p.posti) || 0;
    let yy = first ? 36 : doc.lastAutoTable.finalY + 10;
    if (!first && yy > H - 40) { doc.addPage(); yy = 20; }
    const match = reg.length === posti && posti > 0;
    doc.setFontSize(11.5); doc.setTextColor(30, 30, 30); doc.setFont("helvetica", "bold");
    doc.text(p.nome || "—", M, yy);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.setTextColor(match ? 5 : 180, match ? 130 : 90, match ? 90 : 20);
    doc.text(`${reg.length} / ${posti} posti`, W - M, yy, { align: "right" });
    autoTable(doc, {
      head: [["#", "Ospite", "Camera"]],
      body: reg.length ? reg.map((o, i) => [String(i + 1), [o.cognome, o.nome].filter(Boolean).join(" ") || "—", o.camera ? `Camera ${o.camera}` : ""]) : [["", "Nessun registrato in questo mese", ""]],
      startY: yy + 2,
      styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 248, 252] },
      columnStyles: { 0: { cellWidth: 12, halign: "center" }, 2: { cellWidth: 36 } },
      margin: { left: M, right: M },
    });
    first = false;
  });
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · Registro ingressi · pag. ${i}/${pages} · ${stamp()}`, M, H - 8); }
  doc.save(`registro-ingressi-${safe(meseLabel)}.pdf`);
}


// Report del giorno completo (Supervisione ospiti): da-completare, attività registrate, cadute 60gg, consegne.
export function exportReportGiorno({ data, dData = {}, strutturaNome = "", color = "#0f766e", logo = "", nonSvolte = [], segnalazioni = [] }) {
  const rgb = hexToRgb(color);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
  const M = 14, CW = W - 2 * M;
  const giorno = (() => { try { return new Date((data || "") + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return data || ""; } })();
  const giornoCap = giorno ? giorno.charAt(0).toUpperCase() + giorno.slice(1) : "";
  _pdfBrandHeader(doc, { W, rgb, brand: "Report del giorno", title: giornoCap, logo, barH: 24 });
  let y = 32;
  const meta = [strutturaNome, `Ospiti: ${dData.tot_ospiti ?? "-"}`, `Presenti: ${dData.presenti ?? "-"}`].filter(Boolean).join("   ·   ");
  doc.setTextColor(70, 70, 70); doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.text(meta, M, y); y += 4;
  doc.setTextColor(130); doc.setFontSize(8); doc.text(`Esportato il ${stamp()}`, M, y); y += 7;

  const ensure = (need) => { if (y > H - need) { doc.addPage(); y = 20; } };
  const sectionTitle = (t, sub) => {
    ensure(18); doc.setFillColor(240, 244, 242); doc.rect(M - 2, y - 4, CW + 4, 8, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.text(t, M, y + 1.6);
    if (sub) { doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(120); doc.text(sub, W - M, y + 1.6, { align: "right" }); }
    y += 11;
  };

  // Da completare
  const totM = dData.tot_mancante || 0;
  sectionTitle("Da completare", totM === 0 ? "Tutto a posto" : `${totM} da verificare`);
  if (totM === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(120); doc.text("Tutte le attività previste risultano registrate.", M, y); y += 7;
  } else {
    (dData.da_completare || []).forEach((sec) => {
      if (!sec.items || !sec.items.length) return;
      ensure(12); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(190, 100, 20); doc.text(`${sec.label} (${sec.items.length})`, M, y); y += 5;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(40, 40, 40);
      sec.items.forEach((it) => {
        ensure(8);
        const line = `•  ${it.ospite || ""}${it.dettaglio ? ` — ${it.dettaglio}` : ""}`;
        const wrapped = doc.splitTextToSize(line, CW - 4);
        doc.text(wrapped, M + 2, y); y += wrapped.length * 4.6;
      });
      y += 2;
    });
  }
  y += 2;

  // Attività registrate (box)
  (dData.boxes || []).forEach((b) => {
    sectionTitle(b.label, `${b.count} ${b.count === 1 ? "voce" : "voci"}`);
    if (!b.count) { doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(140); doc.text("Nessuna registrazione.", M, y); y += 7; return; }
    const body = (b.items || []).map((it) => [
      [it.ospite, it.dettaglio].filter(Boolean).join(it.ospite && it.dettaglio ? " — " : ""),
      [it.operatore, it.ora].filter(Boolean).join(" · "),
    ]);
    autoTable(doc, {
      startY: y, head: [["Dettaglio", "Operatore"]], body,
      styles: { fontSize: 8.5, cellPadding: 1.8, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 248, 246] },
      columnStyles: { 0: { cellWidth: CW * 0.68 }, 1: { cellWidth: CW * 0.32 } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 7;
  });

  // Cadute ultimi 60 giorni
  const cad = dData.cadute_60gg || [];
  if (cad.length) {
    sectionTitle("Cadute ultimi 60 giorni", `${cad.length}`);
    autoTable(doc, {
      startY: y, head: [["Data", "Ospite", "Esito", "Operatore"]],
      body: cad.map((c) => [c.data_label || c.data || "", c.ospite || "", [c.esito, c.ora ? `ore ${c.ora}` : ""].filter(Boolean).join(" · "), c.operatore || ""]),
      styles: { fontSize: 8.5, cellPadding: 1.8, overflow: "linebreak" },
      headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 248, 246] },
      columnStyles: { 0: { cellWidth: 22 } }, margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 7;
  }

  // Consegne dei turni
  sectionTitle("Consegne dei turni");
  (dData.consegne || []).forEach((t) => {
    ensure(10); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(60, 60, 60); doc.text(`${t.label} (${(t.items || []).length})`, M, y); y += 5;
    if (!(t.items || []).length) { doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(150); doc.text("Nessuna consegna.", M + 2, y); y += 6; return; }
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(40, 40, 40);
    t.items.forEach((it) => {
      ensure(8);
      const head = [it.ora, it.operatore].filter(Boolean).join(" · ");
      const line = `•  ${head ? head + " — " : ""}${it.testo || ""}`;
      const wrapped = doc.splitTextToSize(line, CW - 4);
      doc.text(wrapped, M + 2, y); y += wrapped.length * 4.6;
    });
    y += 2;
  });

  // Mansioni obbligatorie non svolte + segnalazioni del giorno
  y += 3;
  sectionTitle("Mansioni obbligatorie non svolte", `${nonSvolte.length}`);
  if (!nonSvolte.length) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(150); doc.text("Nessuna mansione obbligatoria non svolta.", M + 2, y); y += 7;
  } else {
    const c = (o, k) => (o.conteggi || {})[k] || 0;
    autoTable(doc, {
      startY: y, head: [["Operatore", "Ritardi", "Ingressi mancanti", "Dashboard non viste", "Consegne non viste", "Totale"]],
      body: nonSvolte.map((o) => { const v = [c(o, "Ritardo"), c(o, "Ingresso non registrato"), c(o, "Dashboard non vista"), c(o, "Consegne non viste")]; return [o.nome, ...v, v.reduce((a, b) => a + b, 0)]; }),
      styles: { fontSize: 8.5, cellPadding: 1.8, halign: "center" },
      headStyles: { fillColor: [190, 18, 60], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [255, 241, 242] },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } }, margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 7;
  }
  sectionTitle("Segnalazioni operatori del giorno", `${segnalazioni.length}`);
  if (!segnalazioni.length) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(150); doc.text("Nessuna segnalazione registrata in questo giorno.", M + 2, y); y += 7;
  } else {
    autoTable(doc, {
      startY: y, head: [["Operatore", "Motivo", "Turno", "Segnalato da"]],
      body: segnalazioni.map((s) => [s.operatore_nome || "", s.motivo || "", s.turno ? s.turno.charAt(0).toUpperCase() + s.turno.slice(1) : "Non in turno", s.creato_da || ""]),
      styles: { fontSize: 8.5, cellPadding: 1.8, overflow: "linebreak" },
      headStyles: { fillColor: rgb, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 248, 246] }, margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 7;
  }

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150); doc.text(`Gestione Residenze · Report del giorno · ${giornoCap} · pag. ${i}/${pages}`, M, H - 8); }
  doc.save(`report-giorno-${safe((strutturaNome ? strutturaNome + "-" : "") + (data || ""))}.pdf`);
}
